import React, { useCallback, useEffect, useRef, useState } from "react";
import { usePageWrapper } from "../../../hooks";
import { Box } from "@mui/material";
import {
  ArtButton,
  ArtDialog,
  useApiService,
  useIdpService,
} from "@art/shared-ui";
import {
  buildPaymentAttemptStorageKey,
  clearPaymentAttemptKey,
  getPaymentAttemptKey,
  DowngradeConfirmationDialog,
  DowngradeRetainSelectionDialog,
  DowngradeScheduledDialog,
  ManageSubscriptionDialog,
  OrderSummaryDialog,
  PaymentFailureDialog,
  PaymentSuccessDialog,
  UpgradeRestoreSelectionDialog,
} from "./helpers";

import { useLocation, useNavigate } from "react-router-dom";

interface BillingScheduleStatus {
  billing_schedule_id?: string;
  status: "active" | "paused" | "failed" | "cancelled";
  attempted_at?: string;
  next_retry_at?: string;
  grace_period_ends_at?: string;
  grace_days_left?: number;
  retry_count?: number;
  amount?: number;
  currency?: string;
  failure_reason?: string;
  plan?: {
    type: string;
    priceId: string;
    planTitle: string;
    price: number;
    currency: string;
  };
}
import { useDispatch, useSelector } from "react-redux";
import { RootState, updateLicenseState } from "../../../store";
import { useAttributePermission } from "../../permissions";
import {
  applicationCodeForPath,
  licenseStateFromProfile,
  selectApplicationLicense,
} from "../../../utils/license";
import ProductSettingsLoader from "../../remote-loaders/product-settings-loader";

interface DialogData {
  title: string;
  content: React.ReactNode;
}

type UpgradeStep =
  | "checkout"
  | "billing"
  | "downgrade_confirm"
  | "downgrade_retain"
  | "downgrade_scheduled"
  | "upgrade_restore"
  | null;

const ArtBillingOverview = () => {
  const canManagePayments = useAttributePermission("PLATFORM_SETTINGS", "BILLING_USAGE", "ManagePayments");
  const canChangeSubscription = useAttributePermission("PLATFORM_SETTINGS", "BILLING_USAGE", "ChangeSubscription");
  const apiservice = useApiService();
  const idpservice = useIdpService();
  const navigate = useNavigate();
  const tenantName = useSelector((state: RootState) => state.auth.tenantName);
  const [activeAddons, setActiveAddons] = useState<any[]>([]);

  const [open, setOpen] = useState(false);
  const [dialogData, setDialogData] = useState<DialogData | null>(null);
  const [, setSubscriptionData] = useState<any>(null);
  const [selectedPlan, setSelectedPlan] = useState<any | null>(null);
  const userPromise = useSelector((state: RootState) => state.auth.user);
  const [isPaying, setIsPaying] = useState(false);
  const [upgradeStep, setUpgradeStep] = useState<UpgradeStep>(null);

  const [scheduledDowngrades, setScheduledDowngrades] = useState<any[]>([]);
  const tenant_name = useSelector((state: RootState) => state.auth.tenantName);

  const { licenseConfig } = useSelector((state: RootState) => state.license);
  const [, setGstNumber] = useState<string>("");

  const location = useLocation();
  const [billingSchedules, setBillingSchedules] = useState<
    BillingScheduleStatus[]
  >([]);
  const [paymentFailureOpen, setPaymentFailureOpen] = useState(false);
  const paymentFailureDismissedRef = useRef(false);

  const retainConfigRef = useRef<{
    channelLimits: Record<string, number>;
    preloadedChannels: any[];
    showChannels: boolean;
  } | null>(null);

  const currentPlan = licenseConfig;
  const dispatch = useDispatch();
  const applicationCode = applicationCodeForPath(location.pathname);

  const withLicenseContext = (license: any) =>
    license
      ? {
          ...license,
          licenses: currentPlan?.licenses || [],
        }
      : null;

  const productPlan = withLicenseContext(
    selectApplicationLicense(
      {
        licenses: currentPlan?.licenses?.length
          ? currentPlan.licenses
          : currentPlan
            ? [currentPlan]
            : [],
      },
      applicationCode,
    ),
  );

  const buildSubscriptionPaymentAttemptKey = (plan: any) =>
    buildPaymentAttemptStorageKey(
      "subscription",
      tenant_name || "",
      plan?.type || "",
      plan?.priceId || "",
    );

  const initiateSubscriptionPayment = async (
    payload: any,
    storageKey: string,
    retry = true,
  ) => {
    const paymentAttemptKey = getPaymentAttemptKey(storageKey);
    try {
      return await apiservice.post(
        `/payment/subscription/initiate`,
        { ...payload, idempotency_key: paymentAttemptKey },
        {
          headers: {
            "Idempotency-Key": paymentAttemptKey,
          },
        },
      );
    } catch (error: any) {
      const data = error?.response?.data || {};
      const errorCode = data.error_code || data.code || data.errorCode;

      if (retry && errorCode === "PAYMENT_ATTEMPT_EXPIRED") {
        clearPaymentAttemptKey(storageKey);
        return initiateSubscriptionPayment(payload, storageKey, false);
      }

      if (retry && errorCode === "IDEMPOTENCY_KEY_REQUIRED") {
        clearPaymentAttemptKey(storageKey);
        return initiateSubscriptionPayment(payload, storageKey, false);
      }

      if (errorCode === "PAYMENT_ATTEMPT_CLOSED") {
        clearPaymentAttemptKey(storageKey);
        apiservice.showSnack(
          "This payment attempt has been closed. Please start again.",
          "error",
        );
      }

      if (errorCode === "IDEMPOTENCY_KEY_REUSED") {
        clearPaymentAttemptKey(storageKey);
        apiservice.showSnack(
          "The payment attempt key was reused. Please start a fresh payment attempt.",
          "error",
        );
      }

      throw error;
    }
  };


  const cancelPaymentAttempt = (orderId?: string, reason?: string) => {
    if (!orderId) return;
    apiservice
      .post("/payment/subscription/attempt/cancel", {
        razorpay_order_id: orderId,
        reason,
      })
      .catch((err: any) => {
        console.error("Failed to record payment attempt cancellation:", err);
      });
  };

  const handleOpenDialog = (title: string, content: any) => {
    setDialogData({
      title,
      content: typeof content === "function" ? content() : content,
    });
    setOpen(true);
  };

  const handleClose = () => {
    const wasPaymentSuccess = upgradeStep === "billing";
    const wasDowngradeScheduled = upgradeStep === "downgrade_scheduled";
    setOpen(false);
    setDialogData(null);
    setUpgradeStep(null);

    if (wasDowngradeScheduled) {
      fetchScheduledDowngrade();
    }

    // Re-fetch license state on closing the payment success dialog
    if (wasPaymentSuccess) {
      apiservice
        .get("/user/profile")
        .then(({ data: { data } }: any) => {
          dispatch(updateLicenseState(licenseStateFromProfile(data, applicationCode)));
        })
        .catch((err: any) => {
          console.error("Failed to refresh license state:", err);
        });
      // Re-check billing-schedule status — a successful payment should clear
      // the autopay-failure state, so the dialog stays closed on remount.
      paymentFailureDismissedRef.current = false;
      fetchBillingScheduleStatus();
    }
  };

  useEffect(() => {
    if (tenantName) {
      idpservice
        .get(`api/tenants/${tenantName}/addons/active?application_code=${applicationCode}`)
        .then((response: any) => {
          setActiveAddons(response?.data?.data || []);
        })
        .catch((error: any) => {
          console.error("Error fetching active addons:", error);
          setActiveAddons([]);
        });
    }
  }, [applicationCode, idpservice, tenantName]);
  // Fetch GST number
  useEffect(() => {
    apiservice
      .get("/organisation/gst-number", { showErrorSnack: false })
      .then((res: any) => {
        setGstNumber(res?.data?.data?.gst_number || "");
      })
      .catch((err: any) => {
        console.error("Error fetching GST number:", err);
      });
  }, []);



  const openCheckout = (plan: any) => {
    setUpgradeStep("checkout");
    handleOpenDialog(
      "Complete Your Purchase",
      <OrderSummaryDialog
        selectedPlan={plan}
        currentPlan={productPlan || currentPlan}
        onProceedToPayment={(p: any, preview: any, billingAddress: any) =>
          handleRazorpayPayment(p, preview, billingAddress)
        }
        onBack={handleClose}
      />,
    );
  };

  // ============== DOWNGRADE FLOW ==============

  const openDowngradeConfirm = (
    plan: any,
    activeSubscription?: any,
    isSubmitting = false,
  ) => {
    setUpgradeStep("downgrade_confirm");
    const effectiveDate =
      activeSubscription?.expires_at ||
      currentPlan?.expiry_date ||
      currentPlan?.expires_at ||
      null;
    handleOpenDialog(
      "Confirm Downgrade",
      <DowngradeConfirmationDialog
        selectedPlan={plan}
        currentPlan={activeSubscription || currentPlan}
        effectiveDate={effectiveDate}
        onBack={handleClose}
        isSubmitting={isSubmitting}
        onConfirm={() =>
          checkAndOpenRetainDialog(plan, effectiveDate, activeSubscription)
        }
      />,
    );
  };

  const featureCodeToChannelType: Record<string, string> = {
    BROADCAST_CHANNEL_LIMIT: "broadcast",
    GROUP_CHANNEL_LIMIT: "group",
    TARGETED_CHANNEL_LIMIT: "targeted",
    ENCRYPTED_CHANNEL_LIMIT: "secure",
    SHARED_OBJECT_CHANNEL_LIMIT: "shared-object",
  };

  const checkAndOpenRetainDialog = async (
    plan: any,
    effectiveDate: any,
    activeSubscription?: any,
  ) => {
   
    const channelLimits: Record<string, number> = {};

    for (const rule of plan?.featureRules ?? []) {
      const channelType = featureCodeToChannelType[rule?.feature?.code];
      if (channelType !== undefined) {
        channelLimits[channelType] = rule.base_limit;
      }
    }
    if (Object.keys(channelLimits).length === 0) {
      await submitRetainAndDowngrade(plan, effectiveDate, [], activeSubscription, true, false);
      return;
    }

    // Show loading on confirm dialog while we fetch
    openDowngradeConfirm(plan, activeSubscription, true);

    let fetchedChannels: any[] = [];

    try {
      const channelTypes = ["targeted", "secure", "broadcast", "group", "shared-object"];
      const channelResults = await Promise.all(
        channelTypes.map(type => apiservice.get(`/channels/${type}?limit=500`)),
      );
      fetchedChannels = channelResults.flatMap(res =>
        res?.data?.data?.channels ?? res?.data?.data ?? []
      );

    } catch (err: any) {
      apiservice.showSnack(
        err?.response?.data?.message ||
        "Failed to load resources. Please try again.",
        "error",
      );
      openDowngradeConfirm(plan, activeSubscription, false);
      return;
    }

    const channelsNeedSelection = Object.entries(channelLimits).some(
      ([type, limit]) =>
        limit >= 0 && fetchedChannels.filter((c: any) => c.type_of_channel === type).length >
        limit,
    );

    if (!channelsNeedSelection) {
      const allChannelIds = fetchedChannels
        .map((c: any) => c?.id || c?._id)
        .filter(Boolean);
      await submitRetainAndDowngrade(
        plan,
        effectiveDate,
        allChannelIds,
        activeSubscription,
        true,
      );
      return;
    }


    retainConfigRef.current = {
      channelLimits,
      preloadedChannels: fetchedChannels,
      showChannels: channelsNeedSelection,
    };

    openRetainDialog(plan, effectiveDate, activeSubscription, false);
  };

  const openRetainDialog = (
    plan: any,
    effectiveDate: any,
    activeSubscription: any,
    isSubmitting: boolean,
  ) => {
    const config = retainConfigRef.current;
    setUpgradeStep("downgrade_retain");
    handleOpenDialog(
      "Choose what to keep",
      <DowngradeRetainSelectionDialog
        selectedPlan={plan}
        effectiveDate={effectiveDate}
        channelLimits={config?.channelLimits ?? {}}
        preloadedChannels={config?.preloadedChannels ?? null}
        showChannels={config?.showChannels ?? true}
        onBack={() => openDowngradeConfirm(plan, activeSubscription, false)}
        isSubmitting={isSubmitting}
        onConfirm={(
          retainedChannelIds: string[],
        ) =>
          submitRetainAndDowngrade(
            plan,
            effectiveDate,
            retainedChannelIds,
            activeSubscription,
          )
        }
      />,
    );
  };

  const submitRetainAndDowngrade = async (
    plan: any,
    effectiveDate: any,
    retainedChannelIds: string[],
    activeSubscription?: any,
    skipRetainDialog = false,
    retainChannels = true,
  ) => {
    if (!plan?.id) return;

    if (!skipRetainDialog) {
      openRetainDialog(plan, effectiveDate, activeSubscription, true);
    }

    try {
      if (retainChannels) {
        await apiservice.post("/downgrade/retain-resources", { channel_ids: retainedChannelIds });
      }
    } catch (error: any) {
      console.error("Failed to flag retained resources:", error);
      apiservice.showSnack(
        error?.response?.data?.message ||
        "Failed to save your selection. Please try again.",
        "error",
      );
      if (skipRetainDialog) {
        openDowngradeConfirm(plan, activeSubscription, false);
      } else {
        openRetainDialog(plan, effectiveDate, activeSubscription, false);
      }
      return;
    }

    try {
      await idpservice.post(
        `api/tenants/${tenant_name}/subscription/downgrade/schedule`,
        {
          tenant_name: tenantName,
          downgrade_plan_id: plan.id,
        },
      );

      fetchScheduledDowngrade();

      setUpgradeStep("downgrade_scheduled");
      handleOpenDialog(
        "Downgrade Scheduled",
        <DowngradeScheduledDialog
          selectedPlan={plan}
          effectiveDate={effectiveDate}
          onClose={handleClose}
        />,
      );
    } catch (error: any) {
      console.error("Failed to schedule downgrade:", error);
      apiservice.showSnack(
        error?.response?.data?.message ||
        "Failed to schedule downgrade. Please try again.",
        "error",
      );
      if (skipRetainDialog) {
        openDowngradeConfirm(plan, activeSubscription, false);
      } else {
        openRetainDialog(plan, effectiveDate, activeSubscription, false);
      }
    }
  };

  const fetchScheduledDowngrade = async () => {
    if (!tenantName) return;
    try {
      const response = await idpservice.get(
        `api/tenants/${tenant_name}/subscription/downgrade`,
      );
      setScheduledDowngrades(response?.data?.data || []);
    } catch (error) {
      setScheduledDowngrades([]);
    }
  };

  const getScheduledDowngradeForLicense = (license: any) => {
    const categoryId = license?.application?.id;
    if (!categoryId) return null;
    return (
      scheduledDowngrades.find(
        (d: any) => d?.downgrade_plan?.application?.id === categoryId,
      ) || null
    );
  };

  useEffect(() => {
    fetchScheduledDowngrade();
  }, [tenantName]);


  const pickMostSevereFailedSchedule = (
    schedules: BillingScheduleStatus[],
  ): BillingScheduleStatus | null => {
    const failed = schedules.filter((s) => s.status === "failed");
    if (failed.length === 0) return null;
    return failed.reduce((worst, current) =>
      (current.grace_days_left ?? Infinity) < (worst.grace_days_left ?? Infinity)
        ? current
        : worst,
    );
  };

  const fetchBillingScheduleStatus = useCallback(async () => {
    if (!tenantName) return null;
    try {
      const response: any = await apiservice.get(
        `/payment/subscription/billing-schedule/status`,
        { showErrorSnack: false },
      );
      const data: BillingScheduleStatus[] = response?.data?.data || [];
      setBillingSchedules(data);
      return data;
    } catch (error) {
      console.error("Failed to fetch billing schedule status:", error);
      return null;
    }
  }, [tenantName, apiservice]);

  useEffect(() => {
    let cancelled = false;
    fetchBillingScheduleStatus().then((data) => {
      if (cancelled || !data) return;
      const hasFailure = pickMostSevereFailedSchedule(data) !== null;
      const arrivedFromRenewLink =
        new URLSearchParams(location.search).get("action") === "renew";
      if (
        hasFailure &&
        (!paymentFailureDismissedRef.current || arrivedFromRenewLink)
      ) {
        setPaymentFailureOpen(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [fetchBillingScheduleStatus, location.search]);

  const failedSchedule = pickMostSevereFailedSchedule(billingSchedules);

  const handlePaymentFailureRetry = () => {
    const failurePlan = failedSchedule?.plan;
    if (!failurePlan) {
      apiservice.showSnack(
        "Unable to load plan details. Please try again or change your plan.",
        "error",
      );
      return;
    }
    setPaymentFailureOpen(false);
    setSelectedPlan(failurePlan);
    openCheckout(failurePlan);
  };

  const handlePaymentFailureChangePlan = () => {
    setPaymentFailureOpen(false);
    apiservice.showSnack(
      "Choose another plan from the application plans on this page.",
      "info",
    );
  };

  const handlePaymentFailureDismiss = () => {
    paymentFailureDismissedRef.current = true;
    setPaymentFailureOpen(false);
  };

  const loadRazorpayScript = (): Promise<boolean> => {
    return new Promise((resolve) => {
      if ((window as any).Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleRazorpayPayment = async (
    plan: any,
    _existingPreview?: any,
    billingAddress?: any,
  ) => {
    if (!canManagePayments || !canChangeSubscription) return;
    setIsPaying(true);
    const activePlan = plan || selectedPlan;
    const contact: string = billingAddress?.contact || "";

    try {
      const storageKey = buildSubscriptionPaymentAttemptKey(activePlan);
      const subscriptionPayload = {
        license_plan_code: activePlan?.type,
        pricing_id: activePlan?.priceId,
        email: userPromise?.email,
        contact,
      };

      const subscriptionResponse = await initiateSubscriptionPayment(
        subscriptionPayload,
        storageKey,
      );

      const subscriptionData = subscriptionResponse?.data?.data;
      if (!subscriptionData?.order_id) {
        apiservice.showSnack("Failed to create payment order", "error");
        setIsPaying(false);
        return;
      }

      // Load Razorpay script
      const isLoaded = await loadRazorpayScript();
      if (!isLoaded) {
        apiservice.showSnack(
          "Failed to load payment gateway. Please try again.",
          "error",
        );
        setIsPaying(false);
        return;
      }

      const options = {
        key: subscriptionData.razorpay_key_id,
        name: "ART Console",
        description: subscriptionData.is_upgrade
          ? `Upgrade to ${activePlan?.planTitle}`
          : `Subscribe to ${activePlan?.planTitle}`,
        order_id: subscriptionData.order_id,
        customer_id: subscriptionData.customer_id,
        recurring: "1",
        prefill: {
          name: userPromise?.first_name || "",
          email: userPromise?.email || "",
          contact: contact,
        },
        theme: { color: "#1976d2" },
        modal: {
          ondismiss: () => {
            setIsPaying(false);
            apiservice.showSnack("Payment cancelled", "info");
            cancelPaymentAttempt(
              subscriptionData.order_id,
              "user_dismissed_checkout",
            );
            clearPaymentAttemptKey(storageKey);
          },
        },
        handler: async (response: any) => {
          await verifyOrderPayment(response, activePlan);
        },
      };

      const razorpay = new (window as any).Razorpay(options);
      razorpay.open();
    } catch (error: any) {
      console.error("Error initiating payment:", error);
      apiservice.showSnack(
        error?.response?.data?.message ||
        "Failed to initiate payment. Please try again.",
        "error",
      );
      setIsPaying(false);
    }
  };


  const openUpgradeRestoreDialogIfAvailable = async (
    activePlan: any,
    paymentId: string,
  ): Promise<boolean> => {
    try {
      const response = await apiservice.get("/upgrade/restore-status");
      const restoreData = { ...response?.data?.data, projects: [] };
      const hasItems = (restoreData?.channels?.length ?? 0) > 0;
      if (!hasItems) {
        return false;
      }
      openUpgradeRestoreDialog(restoreData, activePlan, paymentId);
      return true;
    } catch (err: any) {
      console.error("Failed to fetch upgrade restore status:", err);
      apiservice.showSnack(
        err?.response?.data?.message ||
          "Could not check for items to restore. You can manage them later from Settings → Billing.",
        "warning",
      );
      return false;
    }
  };

  const openUpgradeRestoreDialog = (
    restoreData: any,
    activePlan: any,
    paymentId: string,
    isSubmitting = false,
  ) => {
    setUpgradeStep("upgrade_restore");
    handleOpenDialog(
      "Restore your channels",
      <UpgradeRestoreSelectionDialog
        restoreData={restoreData}
        isSubmitting={isSubmitting}
        onSkip={() => openPaymentSuccess(activePlan, paymentId)}
        onConfirm={(_projectIds: string[], channelIds: string[]) =>
          submitUpgradeRestore(
            restoreData,
            channelIds,
            activePlan,
            paymentId,
          )
        }
      />,
    );
  };

  const submitUpgradeRestore = async (
    restoreData: any,
    channelIds: string[],
    activePlan: any,
    paymentId: string,
  ) => {
    openUpgradeRestoreDialog(restoreData, activePlan, paymentId, true);
    try {
      await apiservice.post("/upgrade/restore-resources", {
        channel_ids: channelIds,
      });
      openPaymentSuccess(activePlan, paymentId);
    } catch (err: any) {
      apiservice.showSnack(
        err?.response?.data?.message ||
          "Failed to restore the selected resources. Please try again.",
        "error",
      );
      openUpgradeRestoreDialog(restoreData, activePlan, paymentId, false);
    }
  };

  const openPaymentSuccess = (activePlan: any, paymentId: string) => {
    setUpgradeStep("billing");
    handleOpenDialog(
      "Payment Successful",
      <PaymentSuccessDialog
        selectedPlan={activePlan}
        paymentId={paymentId}
        onClose={handleClose}
      />,
    );
  };

  // Verify order-based payment (used for upgrades with prorated amount)
  const verifyOrderPayment = async (response: any, activePlan: any) => {
    try {
      const verifyPayload = {
        razorpay_order_id: response.razorpay_order_id,
        razorpay_payment_id: response.razorpay_payment_id,
        razorpay_signature: response.razorpay_signature,
      };

      const verifyResponse = await apiservice.post(
        `payment/subscription/verify`,
        verifyPayload,
      );

      if (verifyResponse?.status === 200 || verifyResponse?.status === 201) {
        clearPaymentAttemptKey(
          buildPaymentAttemptStorageKey(
            "subscription",
            tenant_name || "",
            activePlan?.type || "",
            activePlan?.priceId || "",
          ),
        );

        // Refresh license state after backend processes the upgrade
        try {
          const {
            data: { data },
          } = await apiservice.get("/user/profile");
        dispatch(updateLicenseState(licenseStateFromProfile(data)));
        } catch (err) {
          console.error("Failed to refresh license state after upgrade:", err);
        }

        const restoreSelectionShown = await openUpgradeRestoreDialogIfAvailable(
          activePlan,
          response.razorpay_payment_id,
        );
        if (restoreSelectionShown) {
          return;
        }

        setUpgradeStep("billing");
        handleOpenDialog(
          "Payment Successful",
          <PaymentSuccessDialog
            selectedPlan={activePlan}
            paymentId={response.razorpay_payment_id}
            onClose={handleClose}
          />,
        );
      }
    } catch (verifyError: any) {
      console.error("Order payment verification failed:", verifyError);

      const message: string = verifyError?.response?.data?.message || "";

      apiservice.showSnack(
        message || "Payment verification failed. Please contact support.",
        "error",
      );
    } finally {
      setIsPaying(false);
    }
  };

  const productDowngrade = productPlan
    ? getScheduledDowngradeForLicense(productPlan)
    : null;

  const openProductSubscriptionManagement = () => {
    if (!productPlan) return;
    handleOpenDialog(
      "Manage subscription",
      <ManageSubscriptionDialog
        currentPlan={productPlan}
        handleClose={handleClose}
        onDowngrade={(activeSubscription: any) => {
          handleClose();
          apiservice.showSnack(
            `Choose a lower ${activeSubscription?.application?.name || productPlan?.application?.name || "application"} plan on this page.`,
            "info",
          );
        }}
      />,
    );
    setSubscriptionData(null);
  };

  return (
    <div>
      <ProductSettingsLoader
        surface="billing-plan"
        currentPlan={productPlan}
        activeAddons={activeAddons}
        scheduledDowngrade={productDowngrade}
        onSelectPlan={
          canManagePayments && canChangeSubscription && productPlan &&
          productDowngrade?.status !== "scheduled" && productDowngrade?.status !== "processing"
            ? (plan: any) => {
                setSelectedPlan(plan);
                openCheckout(plan);
              }
            : undefined
        }
        onSelectDowngrade={
          canChangeSubscription && productPlan &&
          productDowngrade?.status !== "scheduled" && productDowngrade?.status !== "processing"
            ? (plan: any) => {
                setSelectedPlan(plan);
                openDowngradeConfirm(plan, productPlan);
              }
            : undefined
        }
        onManageSubscription={canChangeSubscription ? openProductSubscriptionManagement : undefined}
        onViewUsage={() => navigate("/platform-settings/usage/overview")}
        onViewAddons={() => navigate("/platform-settings/billing/addons")}
      />

      <ArtDialog
        title={dialogData?.title}
        open={open}
        onClose={handleClose}
        size={
          upgradeStep === "billing" || upgradeStep === "downgrade_scheduled"
            ? "medium"
            : "large"
        }
        type="form"
        actionBuilder={() => {
          const isEndStep =
            upgradeStep === "billing" || upgradeStep === "downgrade_scheduled";
          const isStepWithOwnActions =
            upgradeStep === "checkout" ||
            upgradeStep === "downgrade_confirm" ||
            upgradeStep === "downgrade_retain" ||
            upgradeStep === "upgrade_restore";
          return (
            <Box
              sx={{
                display: "flex",
                gap: "15px",
                width: "100%",
                justifyContent: isEndStep ? "end" : "start",
              }}
            >
              {!isEndStep && !isStepWithOwnActions && (
                <ArtButton
                  buttonType="button"
                  dataQa="btn-reset"
                  label="Close"
                  variant="outlined"
                  color="error"
                  onClick={() => handleClose()}
                />
              )}
            </Box>
          );
        }}
      >
        {dialogData?.content}
      </ArtDialog>

      <PaymentFailureDialog
        open={paymentFailureOpen}
        onClose={handlePaymentFailureDismiss}
        onRetry={handlePaymentFailureRetry}
        onChangePlan={handlePaymentFailureChangePlan}
        currentPlan={currentPlan}
        failedPlanTitle={failedSchedule?.plan?.planTitle}
        failedPlanType={failedSchedule?.plan?.type}
        failedPlanPrice={failedSchedule?.plan?.price}
        status={failedSchedule?.status}
        failureReason={failedSchedule?.failure_reason}
        failedAmount={failedSchedule?.amount}
        failedCurrency={failedSchedule?.currency}
        graceDaysLeft={failedSchedule?.grace_days_left}
        graceEndsAt={failedSchedule?.grace_period_ends_at}
        failureAttemptedAt={failedSchedule?.attempted_at}
        isHardBlocking={(failedSchedule?.grace_days_left ?? Infinity) <= 1}
        isProcessing={isPaying}
      />
    </div>
  );
};

export default usePageWrapper(ArtBillingOverview, {
  page_title: "Billings & Subscription",
});
